import { SendOutlined } from '@ant-design/icons';
import { type ComunicazioneDTO } from '@framework/api-client/api/backoffice/schemas/comunicazioneDTO.ts';
import { type TipoComunicazioneDTO } from '@framework/api-client/api/inquiries-compile/schemas/tipoComunicazioneDTO.ts';
import { ManagedSubmitButton } from '@framework/form/form/submit/ManagedSubmitButton.tsx';
import { message } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useCommunicationsContext } from '../context/CommunicationsContext.tsx';
import { useSelectedCommunicationType } from './hooks/useSelectedCommunicationType.tsx';

export interface ICommunicationSendButtonProps {
  isManual: boolean | null | undefined;
  sendableTypes: TipoComunicazioneDTO[];
  onClose?: () => void;

  loading?: boolean;
  setIsLoading: (loading: boolean) => void;
}

export function CommunicationSendButton(props: ICommunicationSendButtonProps) {
  const { onClose, isManual, sendableTypes, loading, setIsLoading } = props;
  const { t } = useTranslation();

  const selectedType = useSelectedCommunicationType();

  const { onSend } = useCommunicationsContext();

  const isSendable =
    !isManual && sendableTypes.some(t => t.id === selectedType?.id);

  const handleSend = useCallback(
    async (data: ComunicazioneDTO | null | undefined) => {
      setIsLoading(true);
      try {
        if (!data?.id) {
          // biome-ignore format: <tbd>
          message.error(t('communications.error-saving-communication-on-send', "Si è verificato un errore durante l'invio della comunicazione",));
          return;
        }
        await onSend?.(data.id);
        // biome-ignore format: <tbd>
        message.success(t('communications.sent-successfully', 'Comunicazione inviata con successo'));
        onClose?.();
      } catch (e) {
        // biome-ignore format: <tbd>
        message.error(t( 'communications.error-sending-communication', "Si è verificato un errore durante l'invio della comunicazione",));
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    },
    [onClose, onSend, setIsLoading, t],
  );

  if (!isSendable) {
    return null;
  }

  return (
    <ManagedSubmitButton
      size="large"
      type="primary"
      onValid={handleSend}
      iconRightComponent={SendOutlined}
      iconRightAriaLabel={t('comunicazioni.invia', 'Invia')}
      disabled={loading}
      // Andiamo a impotare il flag "saveOnly" per evitare che la onSubmit
      // faccia operazioni extra che verrano comunque gestite dalla onSend
      transformOnSubmit={data => {
        data.saveOnlyFlag = true;
        return data;
      }}
    >
      {t('comunicazioni.invia', 'Invia')}
    </ManagedSubmitButton>
  );
}
